import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hd2:ownedWarbonds";
const CHANGE_EVENT = "hd2:ownedWarbondsChange";

interface StoredOwnedWarbonds {
	version: 1;
	warbonds: string[];
}

function slugify(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
}

function read(): Set<string> {
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return new Set();
	}
	try {
		const parsed = JSON.parse(raw) as StoredOwnedWarbonds;
		if (parsed.version !== 1) {
			return new Set();
		}
		return new Set(parsed.warbonds);
	} catch {
		return new Set();
	}
}

function write(set: Set<string>) {
	const data: StoredOwnedWarbonds = {
		version: 1,
		warbonds: [...set],
	};
	localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

export interface UseOwnedWarbondsResult {
	owned: Set<string>;
	has(name: string): boolean;
	toggle(name: string): void;
	set(name: string, owned: boolean): void;
	setMany(names: string[], owned: boolean): void;
}

export function useOwnedWarbonds(): UseOwnedWarbondsResult {
	const [owned, setOwned] = useState<Set<string>>(() => read());

	useEffect(() => {
		const sync = () => {
			setOwned(read());
		};
		window.addEventListener("storage", sync);
		window.addEventListener(CHANGE_EVENT, sync);
		return () => {
			window.removeEventListener("storage", sync);
			window.removeEventListener(CHANGE_EVENT, sync);
		};
	}, []);

	const has = useCallback((name: string) => owned.has(slugify(name)), [owned]);

	const toggle = useCallback((name: string) => {
		const current = read();
		const slug = slugify(name);
		if (current.has(slug)) {
			current.delete(slug);
		} else {
			current.add(slug);
		}
		write(current);
	}, []);

	const set = useCallback((name: string, isOwned: boolean) => {
		const current = read();
		const slug = slugify(name);
		if (isOwned) {
			current.add(slug);
		} else {
			current.delete(slug);
		}
		write(current);
	}, []);

	const setMany = useCallback((names: string[], isOwned: boolean) => {
		const current = read();
		for (const name of names) {
			const slug = slugify(name);
			if (isOwned) {
				current.add(slug);
			} else {
				current.delete(slug);
			}
		}
		write(current);
	}, []);

	return { owned, has, toggle, set, setMany };
}
